import clsx from 'clsx'
import type { ElementType, PropsWithChildren } from 'react'
import React from 'react'

interface TypographyProps {
  color?: TypographyColor
  component?: ElementType
  truncate?: boolean
  variant?: TypographyVariant
}

type TypographyColor = 'initial' | 'inherit' | 'inverse'

type TypographyColors = {
  [key in TypographyColor]: string
}

type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'subtitle3'
  | 'body1'
  | 'body2'
  | 'button'
  | 'caption'
  | 'overline'

type TypographyVariants = {
  [key in TypographyVariant]: string
}

const colors: TypographyColors = {
  inherit: 'text-inherit',
  initial: 'text-black-90',
  inverse: 'text-white-90',
}

const variants: TypographyVariants = {
  h1: `text-6xl font-medium tracking-tight leading-tighter`,
  h2: `text-5xl font-medium tracking-tight leading-tight`,
  h3: `text-4xl font-medium tracking-normal leading-tighter`,
  h4: `text-3xl font-medium tracking-normal leading-tight`,
  h5: `text-2xl font-medium tracking-normal leading-snug`,
  h6: `text-xl font-medium tracking-wide leading-normal`,
  subtitle1: `text-base font-medium tracking-normal leading-7`,
  subtitle2: `text-sm font-medium tracking-wide leading-6`,
  subtitle3: `text-xs font-medium tracking-wider leading-loose`,
  body1: `text-base font-normal tracking-normal leading-6`,
  body2: `text-sm font-normal tracking-wide leading-5`,
  button: `text-sm font-medium tracking-wide leading-6`,
  caption: `text-xs font-normal tracking-wider leading-tighter`,
  overline: `text-xs font-medium tracking-widest leading-looser uppercase`,
}

function Typography({
  children,
  color = 'initial',
  component: Component = 'span',
  truncate = false,
  variant = 'body1',
}: PropsWithChildren<TypographyProps>): JSX.Element {
  return (
    <Component
      className={clsx(
        'font-sans',
        colors[color],
        truncate ? 'truncate' : null,
        variants[variant],
      )}
    >
      {children}
    </Component>
  )
}

export default Typography
